import { Button, Col, Form, Input, Modal, notification, Row } from "antd";
import { useTranslation } from "react-i18next";
import {
  useDeleteTreuhaenderContactMailMutation,
  useSetTreuhaenderContactMailMutation,
} from "store/masterdata";
import { useState } from "react";
import ConfirmationModal from "views/App/components/Modals/ConfirmationModal";

interface Props {
  initialValues: {
    email?: string;
  };
  kundennummer: number;
  visible: boolean;
  onClose(): void;
  onSuccess(): void;
}

function ChangeTHContactEmail({
  initialValues,
  kundennummer,
  visible,
  onClose,
  onSuccess,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [treuhaenderContactEmailForm] = Form.useForm();
  const [email, setEmail] = useState(initialValues.email || "");
  const [saveConfirmationModalVisible, setSaveConfirmationModalVisible] =
    useState(false);
  const [deleteConfirmationModalVisible, setDeleteConfirmationModalVisible] =
    useState(false);
  const [setTreuhaenderContactMail, setTreuhaenderContactMailResult] =
    useSetTreuhaenderContactMailMutation();
  const [deleteTreuhaenderContactMail, deleteTreuhaenderContactMailResult] =
    useDeleteTreuhaenderContactMailMutation();
  const handleFormSubmit = (values: { email: string }) => {
    setEmail(values.email);
    setSaveConfirmationModalVisible(true);
  };

  const confirmSetTreuhaenderContactMail = async () => {
    try {
      const response = await setTreuhaenderContactMail({
        kundennummer,
        email,
      }).unwrap();
      if (!response.hasErrors) {
        notification.success({
          message: t("masterdata.lw_details.set_th_contact_mail_success"),
        });
        onSuccess();
        onClose();
      } else {
        notification.error({ message: response.errorMessage });
        onClose();
      }
    } catch (error) {
      console.error("Error", error);
      onClose();
    }
  };

  const confirmDeleteTreuhaenderContactMail = async () => {
    try {
      const response = await deleteTreuhaenderContactMail({
        kundennummer,
      }).unwrap();
      if (!response.hasErrors) {
        notification.success({
          message: t("masterdata.lw_details.delete_th_contact_mail_success"),
        });
        onSuccess();
        onClose();
      } else {
        notification.error({ message: response.errorMessage });
        onClose();
      }
    } catch (error) {
      console.error("Error", error);
      onClose();
    }
  };

  const handleDelete = () => {
    setDeleteConfirmationModalVisible(true);
  };

  return (
    <Modal
      footer={null}
      title={t("masterdata.lw_details.change_th_email_title")}
      visible={visible}
      onCancel={onClose}
      destroyOnClose
    >
      <Form
        id="treuhaender-contact-email-form"
        form={treuhaenderContactEmailForm}
        onFinish={handleFormSubmit}
        layout="vertical"
        initialValues={initialValues}
      >
        <Form.Item
          label={t("masterdata.lw_details.email")}
          name="email"
          rules={[{ required: true, type: "email" }]}
        >
          <Input />
        </Form.Item>
        <Row gutter={20}>
          <Col span={12}>
            <Button onClick={handleDelete} block>
              {t("clear")}
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              htmlType="submit"
              loading={setTreuhaenderContactMailResult.isLoading}
              block
            >
              {t("save")}
            </Button>
          </Col>
        </Row>
      </Form>
      <ConfirmationModal
        visible={saveConfirmationModalVisible}
        isLoading={setTreuhaenderContactMailResult.isLoading}
        title={t(
          "masterdata.lw_details.set_th_contact_mail_confirmation_title"
        )}
        description={t(
          "masterdata.lw_details.set_th_contact_mail_confirmation"
        )}
        onClose={() => setSaveConfirmationModalVisible(false)}
        onConfirm={() => confirmSetTreuhaenderContactMail()}
      />
      <ConfirmationModal
        visible={deleteConfirmationModalVisible}
        isLoading={deleteTreuhaenderContactMailResult.isLoading}
        title={t(
          "masterdata.lw_details.delete_th_contact_mail_confirmation_title"
        )}
        description={t(
          "masterdata.lw_details.delete_th_contact_mail_confirmation"
        )}
        onClose={() => setDeleteConfirmationModalVisible(false)}
        onConfirm={() => confirmDeleteTreuhaenderContactMail()}
      />
    </Modal>
  );
}

export default ChangeTHContactEmail;
